import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import { NotificationService } from '../../../services/notificationService';
import { NotificationPreferences } from '../../../types/user';

/**
 * UserSimulator - Simulate different user preference configurations
 */
const UserSimulator: React.FC = () => {
  const [testUserId] = useState('test-user-simulator-' + Date.now());
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    userId: testUserId,
    enabled: true,
    channels: {
      inApp: { enabled: true },
      email: { enabled: true, digestMode: 'daily', digestTime: '09:00' },
    },
    categories: {
      authentication: true,
      contentModeration: true,
      socialInteractions: true,
      familyTreeUpdates: true,
      researchCollaboration: true,
      reminders: true,
      systemAnnouncements: true,
    },
    updatedAt: new Date(),
    version: 1,
  });

  const [result, setResult] = useState<string | null>(null);

  const handleToggle = (field: keyof NotificationPreferences['categories']) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [field]: !prev.categories[field],
      },
    }));
  };

  const handleMasterToggle = () => {
    setPreferences(prev => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  };

  const testPreferences = async () => {
    try {
      // Save test preferences
      await NotificationService.updatePreferences(testUserId, preferences);

      // Try to send a test notification
      const notification = await NotificationService.createNotification({
        userId: testUserId,
        type: 'system_announcement',
        title: 'Preference Simulation Test',
        message: 'Testing user preference configuration',
      });

      if (notification.id.startsWith('mock-')) {
        setResult('Notification was blocked based on current preferences (mock notification created)');
      } else {
        setResult('Notification would be delivered with current preferences');
      }
    } catch (error: any) {
      setResult(`Error: ${error.message}`);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        User Preference Simulator
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Simulate different user preference configurations to test notification delivery logic
      </Typography>

      <Grid container spacing={3}>
        {/* Master Toggle */}
        <Grid item xs={12}>
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.enabled}
                  onChange={handleMasterToggle}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1">
                    Notifications Enabled
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Master toggle for all notifications
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Category Toggles */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Notification Categories
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(preferences.categories).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={() => handleToggle(key as keyof NotificationPreferences['categories'])}
                      disabled={!preferences.enabled}
                    />
                  }
                  label={key.replace(/([A-Z])/g, ' $1').trim()}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Test Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            onClick={testPreferences}
          >
            Test Current Configuration
          </Button>
        </Grid>

        {/* Result */}
        {result && (
          <Grid item xs={12}>
            <Alert severity={result.includes('blocked') ? 'warning' : 'success'}>
              {result}
            </Alert>
          </Grid>
        )}

        {/* Current Config Summary */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Configuration
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip
                label={preferences.enabled ? 'Enabled' : 'Disabled'}
                color={preferences.enabled ? 'success' : 'default'}
                size="small"
              />
              {Object.entries(preferences.categories).map(([key, value]) => (
                value && (
                  <Chip
                    key={key}
                    label={key.replace(/([A-Z])/g, ' $1').trim()}
                    size="small"
                    variant="outlined"
                  />
                )
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserSimulator;
